import { isEmpty, has, get, set, omit } from 'lodash';
import { isRichContent } from '../../../services';
const isLegacyImage = (entity) => entity.type === 'IMAGE';
const isImage = (entity) => entity.type === 'wix-draft-plugin-image';
const isGallery = (entity) => entity.type === 'wix-draft-plugin-gallery';
const isSmallSizeGridGalleryWithoutPerRowValue = (entity) => isGallery(entity) &&
    !has(entity, 'data.styles.numberOfImagesPerRow') &&
    get(entity, 'data.styles.galleryLayout') === 2 &&
    get(entity, 'data.config.size') === 'small';
const isVideoEmbed = (entity) => entity.type === 'VIDEO-EMBED';
const isLink = (entity) => entity.type === 'LINK';
const isEntityWithoutData = (entity) => (isLegacyImage(entity) || isVideoEmbed(entity)) && isEmpty(entity.data);
const isImageWithoutSrc = (entity) => isLegacyImage(entity) && !get(entity, 'data.src');
const isImageWithoutConfig = (entity) => isImage(entity) && !get(entity, 'data.config');
const isVideoWithoutUrl = (entity) => isVideoEmbed(entity) && !get(entity, 'data.url');
const isLinkWithoutUrl = (entity) => isLink(entity) && !get(entity, 'data.url');
const isCorrupted = (entity) => isEntityWithoutData(entity) ||
    isImageWithoutSrc(entity) ||
    isVideoWithoutUrl(entity);
const hasOneOfEntities = (block, entityKeys) => entityKeys.indexOf(get(block, 'entityRanges[0].key')) > -1;
function isVersionBefore(before, version) {
    if (isEmpty(version)) {
        return true;
    }
    if (before === version) {
        return false;
    }
    [before, version] = [before.split('.'), version.split('.')];
    for (let i = 0; i < 3; i++) {
        const diff = Number(before[i] || 0) - Number(version[i] || 0);
        if (diff > 0) {
            return true;
        }
        if (diff < 0) {
            return false;
        }
    }
    return false;
}
const getCorrupted = (entityMap) => Object.keys(entityMap).reduce((acc, key) => {
    const entity = entityMap[key];
    if (isCorrupted(entity)) {
        acc.corruptedEntityKeys.push(Number(key));
    }
    else if (isLinkWithoutUrl(entity)) {
        acc.invalidLinkEntityKeys.push(Number(key));
    }
    return acc;
}, { corruptedEntityKeys: [], invalidLinkEntityKeys: [] });
const removeCorrupted = (corruptedEntityKeys, content) => {
    const entityMap = content.entityMap;
    const isValidEntityRange = (entityRange) => {
        const entity = entityMap[entityRange.key];
        return entity && !isLinkWithoutUrl(entity);
    };
    return content.blocks.reduce((acc, block) => {
        if (hasOneOfEntities(block, corruptedEntityKeys)) {
            return acc;
        }
        if (Array.isArray(block.entityRanges) && block.entityRanges.length) {
            block.entityRanges = block.entityRanges.filter(isValidEntityRange);
        }
        acc.push(block);
        return acc;
    }, []);
};
const tempFix = (entityMap, version) => {
    Object.keys(entityMap).forEach((key) => {
        const entity = entityMap[key];
        if (isGallery(entity)) {
            const imageMargin = get(entity, 'data.styles.imageMargin', null);
            if (imageMargin && typeof imageMargin === 'string') {
                entity.data.styles.imageMargin = parseInt(imageMargin, 10);
            }
        }
        if (isImageWithoutConfig(entity)) {
            entity.type = 'IMAGE';
        }
        else if (isSmallSizeGridGalleryWithoutPerRowValue(entity) &&
            isVersionBefore('6.9.2', version)) {
            set(entity, 'data.styles.numberOfImagesPerRow', 2);
        }
    });
    return entityMap;
};
/**
 * Removes invalid entities which were corrupted by bugs in wix-rich-content-editor
 */
export default function removeCorruptedEntities(content) {
    if (typeof content === 'string') {
        return JSON.parse(content);
    }
    if (!content || isRichContent(content)) {
        return content;
    }
    const { corruptedEntityKeys, invalidLinkEntityKeys } = getCorrupted(content.entityMap);
    return {
        ...content,
        blocks: removeCorrupted(corruptedEntityKeys, content),
        entityMap: tempFix(omit(content.entityMap, [
            ...corruptedEntityKeys,
            ...invalidLinkEntityKeys,
        ]), content.VERSION),
    };
}
//# sourceMappingURL=remove-corrupted-entities.js.map